/* ============================================================================
   addonSync — the dashboard's handle on the add-on integrations.

   ⚠️  THE ENGINE IS NOT HERE ANY MORE. It lives on the backend, in
   `pear-payments-backend/src/engine/addons/`, and runs on the 15-minute cron.

   It used to live in this file: ~600 lines that pulled each provider, compared
   the result against what the mobile app had collected, and wrote insights,
   vehicle fuel state, fleet alerts and fee suspensions straight to Firestore
   from the browser. It was driven by `useAddonSyncPoller`, which was mounted on
   exactly one page — `/admin/org/:orgCode/settings` — behind `PearOnlyRoute`,
   a route fleet admins are redirected away from. So a school's connected GPS or
   fuel system only fed PEAR while a PEAR staff member had that org's settings
   page open in a tab, and stopped the moment they closed it.

   Three things follow from moving it:

     1. Connected systems keep flowing with nobody logged in anywhere.
     2. The comparison keeps state between runs, which is what makes an
        odometer usable at all — see the note on lifetime counters in
        `engine/addons/compare.js`.
     3. The browser no longer writes `feeStatus`, so nothing client-side can
        decide a child may not board.

   What remains here is the two things the dashboard genuinely does: verify a
   connection when someone saves credentials, and ask for a sync now.
   ========================================================================== */
import { AddonId } from '../components/addonProviders';
import { testAddonConnection, syncAddonNow, isAddonsConfigured } from './addonsApi';

export type SyncAddonKey = 'gps' | 'fuel' | 'fees';
export const SYNC_ADDON_KEYS: SyncAddonKey[] = ['gps', 'fuel', 'fees'];

interface SyncAddon {
  wanted?: boolean;
  status?: 'default' | 'connected' | 'off';
  /** True only after a "Save & verify" test pulled real records. The backend
   *  sweep refuses to run for an add-on that is not verified. */
  verified?: boolean;
  provider?: string;
  apiUrl?: string;
  apiKey?: string;
  extraFields?: Record<string, string>;
}

export interface SyncOrg {
  code: string;
  name?: string;
  productType?: string;
  addons?: Record<string, SyncAddon>;
}

/** Toggled on, connected, AND verified. Mirrors `isLive` in the backend sweep —
 *  the authoritative copy is there; this one only drives what the UI says. */
export function isAddonLive(addon?: SyncAddon): boolean {
  return !!addon && addon.wanted !== false && addon.status === 'connected' && addon.verified === true;
}

/**
 * Ask the backend to run the sweep for this org now. Resolves either way: a
 * failed sync leaves the previous comparison in place rather than blanking it,
 * exactly as the scheduled run does.
 */
export async function runAddonSync(org: SyncOrg, addonKey?: AddonId): Promise<{ ok: boolean; message: string }> {
  if (!org?.code) return { ok: false, message: 'No organisation selected.' };
  const key = addonKey && SYNC_ADDON_KEYS.includes(addonKey as SyncAddonKey)
    ? (addonKey as SyncAddonKey)
    : undefined;
  return syncAddonNow(org.code, key);
}

export interface VerifyResult {
  ok: boolean;
  sampleCount: number;
  message: string;
}

/**
 * The "Save & verify" gate. Confirms real records come back from the configured
 * provider BEFORE the add-on is marked verified — which is what lets the backend
 * sweep touch it at all. A failure leaves it unverified, so the engine never
 * runs against a broken connection.
 */
export async function verifyAddonConnection(org: SyncOrg, addonKey: AddonId): Promise<VerifyResult> {
  const key = addonKey as SyncAddonKey;
  if (!SYNC_ADDON_KEYS.includes(key)) return { ok: false, sampleCount: 0, message: 'Unknown add-on.' };
  if (!isAddonsConfigured()) {
    return {
      ok: false,
      sampleCount: 0,
      message: 'The integration backend isn’t configured yet, so live connections can’t be verified. Please contact the technical team.',
    };
  }
  return testAddonConnection(org.code, key);
}
